import React from 'react';
import { Moon } from 'lucide-react';

interface ShadowEffectProps {
  isShadow?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  intensity?: 'low' | 'medium' | 'high';
}

const ShadowEffect: React.FC<ShadowEffectProps> = ({
  isShadow = false,
  className = '',
  size = 'medium',
  intensity = 'medium'
}) => {
  if (!isShadow) return null;

  const sizeMap = {
    small: 'w-2 h-2',
    medium: 'w-3 h-3',
    large: 'w-5 h-5'
  };

  const particleCount = {
    low: 2,
    medium: 4,
    high: 6
  };

  const shadowSize = sizeMap[size];
  const count = particleCount[intensity];

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {/* Dark wisps */}
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute animate-pulse"
          style={{
            left: `${15 + (i * 25) % 70}%`,
            top: `${15 + (i * 20) % 70}%`,
            animationDelay: `${i * 500}ms`,
            animationDuration: `${2500 + i * 300}ms`,
            opacity: 0.6
          }}
        >
          <Moon
            className={`text-purple-900 ${shadowSize}`}
            style={{
              filter: 'drop-shadow(0 0 4px rgba(88, 28, 135, 0.8))',
              transform: `rotate(${i * 30}deg)`
            }}
          />
        </div>
      ))}

      {/* Shadow particles */}
      {Array.from({ length: Math.floor(count * 1.5) }).map((_, i) => (
        <div
          key={`particle-${i}`}
          className="absolute animate-ping"
          style={{
            left: `${10 + (i * 20) % 80}%`,
            top: `${10 + (i * 25) % 80}%`,
            animationDelay: `${i * 300 + 700}ms`,
            animationDuration: `${2000 + i * 400}ms`,
            opacity: 0.4
          }}
        >
          <div
            className="w-1.5 h-1.5 bg-purple-950 rounded-full"
            style={{
              filter: 'blur(1px)',
              boxShadow: '0 0 4px rgba(46, 16, 101, 0.9)'
            }}
          />
        </div>
      ))}

      {/* Dark aura */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(88, 28, 135, 0.15) 0%, rgba(46, 16, 101, 0.25) 40%, rgba(0, 0, 0, 0.1) 70%, transparent 90%)',
          animation: 'pulse 3.5s ease-in-out infinite',
          opacity: 0.7
        }}
      />

      {/* Floating dark orbs */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`orb-${i}`}
          className="absolute"
          style={{
            left: `${-3 + (i * 105) % 105}%`,
            top: `${-3 + (i * 80) % 105}%`,
            animation: 'float 4s ease-in-out infinite',
            animationDelay: `${i * 800}ms`,
            opacity: 0.5
          }}
        >
          <div
            className="w-2 h-2 bg-indigo-950 rounded-full"
            style={{
              filter: 'blur(1.5px)',
              boxShadow: '0 0 6px rgba(30, 27, 75, 0.8), 0 0 10px rgba(88, 28, 135, 0.4)'
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default ShadowEffect;
